<template>
  <div class="content-search p-0">
    <div class="d-flex align-items-center mb-3 text-lightblue">
      <span>Keyword Search</span>
      <el-tooltip
        transition=""
      >
        <div
          slot="content"
          class="p-2"
          style="min-width:240px"
        >
          <div class="row">
            <div class="col-12 text-200">
              You can search for one or more keywords and quoted phrases within the transcript text.
              <br><br>
              Separate each keyword or quoted phrase by one or more spaces.
              <br><br>
              A quoted phrase is multiple words wrapped in quotes, for example &quot;you have&quot;.
              <br><br>
              If you enter more than one word or phrase, all instances of each word or phrase will be selected.
              <br><br>
              Scroll through multiple matches using the Next and Prev buttons.
              <br><br>
              Click the (-) button to clear the search.
              <br><br>
            </div>
          </div>
          <div class="row">
            <div class="col-12">
              Examples:
            </div>
          </div>
          <div class="row mt-2">
            <div class="col-3">
              call
            </div>
            <div class="col-9 text-200">
              will find 'call', 'calls', 'calling', etc.
            </div>
          </div>
          <div class="row mt-2">
            <div class="col-3">
              swear bro know
            </div>
            <div class="col-9 text-200">
              will find all words starting with 'swear', 'bro' or 'know'. This will find the word 'brother' as well.
            </div>
          </div>
          <div class="row mt-2">
            <div class="col-3">
              "you know" got
            </div>
            <div class="col-9 text-200">
              finds all instances of 'know' following 'you', as well as 'got', 'gotta', 'gotten', etc.
            </div>
          </div>
        </div>
        <InfoIcon
          size="16"
          class="ml-2 text-lightblue"
        />
      </el-tooltip>
    </div>
    <div>
      <div>
        <input
          v-model="searchTerm"
          type="text"
          class="form-control ethosSearch"
          placeholder="SEARCH"
          @keyup.enter="search"
        >
      </div>
      <div class="d-flex mb-3">
        <base-button
          class="search-btn"
          size="md"
          type="primary"
          icon
          simple
          @click="search"
        >
          <search-icon size="1.1x" />
        </base-button>
        <base-button
          class="clear-btn"
          size="md"
          type="primary"
          icon
          simple
          @click="clear"
        >
          <minus-icon size="1.1x" />
        </base-button>
      </div>
      <div
        v-if="action"
        class="d-flex align-items-center"
      >
        <span class="result pr-sm-2"> {{ current + 1 }} of {{ indices.length }} </span>
        <div class="btn-group">
          <base-button
            size="sm"
            type="secondary"
            simple
            class="btn"
            :disabled="current <= 0"
            @click="previous"
          >
            Prev
          </base-button>
          <base-button
            size="sm"
            type="secondary"
            simple
            class="btn"
            :disabled="current >= indices.length - 1"
            @click="next"
          >
            Next
          </base-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {SearchIcon, MinusIcon, InfoIcon} from "vue-feather-icons";
import {TextSearchMatcher} from "../../../../util/textSearch";
import {mapGetters} from "vuex";

export default {
  name: "content-search",
  components: {SearchIcon, MinusIcon, InfoIcon},
  props: {
    transcriptContent: {
      type: String,
      default: "",
    },
    translationContent: {
      type: String,
      default: null,
    },
    inTranslationMode: {
      type: Boolean,
      default: false,
    },
    metadata: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      searchTerm: "",
      indices: [],
      current: -1,
      action: false,
    };
  },
  methods: {
    ...mapGetters("data", [
      "translationIsActive",
    ]),
    findMatches(searchTerm) {
      const matches = [];
      const lhsMatches = TextSearchMatcher.findMatches(this.transcriptContent, searchTerm);
      if (lhsMatches && lhsMatches.length > 0) {
        lhsMatches.forEach((m) => {
          let speaker = -1;
          const spk = this.metadata.speakers.findLast((s) => s.s <= m.offset);
          if (spk) {
            speaker = spk.s;
          }
          matches.push({speaker: speaker, side: "lhs", offset: m.offset, length: m.length});
        });
      }
      if (this.translationIsActive() && this.inTranslationMode && this.translationContent) {
        const rhsMatches = TextSearchMatcher.findMatches(this.translationContent, searchTerm);
        if (rhsMatches && rhsMatches.length > 0) {
          rhsMatches.forEach((m) => {
            let speaker = -1;
            // offset is one greater because of normalized blank at beginning of span.
            const entry = this.metadata.translationEntries.find((e) => e.s === m.offset - 1);
            if (entry) {
              const spk = this.metadata.speakers.find((s) => s.si === entry.si);
              if (spk) {
                speaker = spk.s;
              }
            }
            matches.push({speaker: speaker, side: "rhs", offset: m.offset, length: m.length});
          });
        }
      }
      matches.sort((a, b) => {
        // Sort by speaker, side, and offset
        if (a.speaker !== b.speaker) {
          return a.speaker - b.speaker;
        }
        if (a.side !== b.side) {
          return a.side.localeCompare(b.side);
        }
        return a.offset - b.offset;
      });

      return matches;
    },
    search() {
      if (this.searchTerm.trim() !== "") {
        this.indices = this.findMatches(this.searchTerm.trim());
        console.log("search indices", this.indices);
        this.current = this.indices.length > 0 ? 0 : -1;
        this.action = this.current === -1 ? false : true;
      } else {
        this.action = false;
        this.indices = [];
        this.current = -1;
        this.searchTerm = "";
      }
      this.$emit("indices", this.indices);
      this.setCurrent(this.current);
    },
    clear() {
      this.action = false;
      this.indices = [];
      this.current = -1;
      this.searchTerm = "";
      this.$emit("indices", this.indices);
      this.setCurrent(this.current);
    },
    previous() {
      this.setCurrent(this.current - 1);
    },
    next() {
      this.setCurrent(this.current + 1);
    },
    setCurrent(index) {
      this.current = Math.max(0, Math.min(this.indices.length - 1, index));
      this.$emit("current", this.current);
      this.$emit("scrollToNext", this.current);
    },
  },
};
</script>

<style scoped>

.search-btn {
  margin-left: 0 !important;
  margin-right: 8px !important;
}

.clear-btn {
  margin-left: 0 !important;
  margin-right: 8px !important;
}

.info-btn {
  margin-left: 0 !important;
}

</style>
